import React from 'react';
import {
  HStack, Text, Pressable, Icon, ScrollView, View, Box,
} from '@gluestack-ui/themed';
import {
  Users,
  GraduationCap,
  UsersRound,
  Boxes,
  CheckSquare,
  FileBarChart,
  BarChart3,
  LucideIcon,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { DrawerContentComponentProps } from '@react-navigation/drawer';

/** Section header component */
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <View px="$4" pt="$4" pb="$1.5">
    <Text
      size="2xs"
      fontWeight="$semibold"
      color="$textSubtle"
      sx={{
        _text: {
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        },
      }}
    >
      {title}
    </Text>
  </View>
);

interface DrawerItemProps {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  onPress: () => void;
}

/** Custom drawer item - matches lecturer sidebar style with left border indicator */
const DrawerItem: React.FC<DrawerItemProps> = ({
  label, icon: IconComponent, isActive, onPress,
}) => (
  <Pressable
    onPress={onPress}
    p="$2.5"
    px="$3"
    my="$0.5"
    ml={isActive ? '$1' : '$1.5'}
    mr="$1.5"
    borderLeftWidth={isActive ? 3 : 0}
    borderLeftColor={isActive ? '$borderBrand' : 'transparent'}
    bg={isActive ? '$bgBrandSubtle' : 'transparent'}
    opacity={isActive ? 1 : 0.8}
  >
    <HStack alignItems="center" space="md">
      <Icon as={IconComponent} size="sm" color={isActive ? '$brandBase' : '$textSubtle'} />
      <Text size="sm" fontWeight={isActive ? '$semibold' : '$medium'} color={isActive ? '$textBrand' : '$textBody'}>
        {label}
      </Text>
    </HStack>
  </Pressable>
);

/** Custom drawer content with sections */
const AdminDrawerContent: React.FC<DrawerContentComponentProps> = ({ state, navigation }) => {
  const { t } = useTranslation();
  const currentRoute = state.routes[state.index]?.name;

  const sections = [
    {
      title: t('admin.navigation.people'),
      items: [
        { name: 'Users', label: t('admin.navigation.users'), icon: Users },
        { name: 'Cohorts', label: t('admin.navigation.cohorts'), icon: UsersRound },
      ],
    },
    {
      title: t('admin.navigation.content'),
      items: [
        { name: 'Courses', label: t('admin.navigation.courses'), icon: GraduationCap },
        { name: 'Modules', label: t('admin.navigation.modules'), icon: Boxes },
      ],
    },
    {
      title: t('admin.navigation.workflows'),
      items: [{ name: 'Approvals', label: t('admin.navigation.approvals'), icon: CheckSquare }],
    },
    {
      title: t('admin.navigation.reports'),
      items: [
        { name: 'Submissions', label: t('admin.navigation.submissions'), icon: FileBarChart },
        { name: 'GradingStats', label: t('admin.navigation.gradingStats'), icon: BarChart3 },
        { name: 'AiTutor', label: t('admin.navigation.aiTutor'), icon: BarChart3 },
      ],
    },
  ];

  return (
    <Box flex={1} borderRightWidth={1} borderRightColor="$borderLight" bg="$white">
      <ScrollView flex={1} pt="$2">
        {sections.map((section) => (
          <View
            key={section.title}
            testID={
              section.title === t('admin.navigation.reports')
                ? 'admin-drawer-reports-section'
                : undefined
            }
          >
            <SectionHeader title={section.title} />
            {section.items.map((item) => (
              <DrawerItem
                key={item.name}
                label={item.label}
                icon={item.icon}
                isActive={currentRoute === item.name}
                onPress={() => navigation.navigate(item.name)}
              />
            ))}
          </View>
        ))}
      </ScrollView>
    </Box>
  );
};

export default AdminDrawerContent;
